import { merge } from 'lodash';
import {
  keyBy,
  prop,
  pickBy,
  has,
} from 'lodash/fp';
import logger from '@/utils/logger';
import fetchService from '@/utils/model/fetch_service';
import globals from '@/globals';
import chipSchema from '@/schemas/chip_schemas';
import columnsSchema from '@/schemas/browser_columns_schemas';
import DefaultColumns from '@/mappings/default_columns';
import { validatePayload, showToast, ascending } from '@/utils/tools';  // eslint-disable-line
import { Tree } from '@/utils/tree';
import { Status } from '@/utils/useData';
import LANG from '@/lang/eng';
import { TreeMode } from './config';

async function fetchColumns(context) {
  const { state, rootState, commit } = context;
  const response = await fetchService.GetData('docbrowser_items_columns');
  const { valid, tree: data } = validatePayload(response && response.data, columnsSchema);
  if (!valid) {
    logger.warn('Invalid column settings', data);
    return;
  }

  const customColumns = keyBy(prop('name'), data.columns || []);
  const { browserMode } = rootState;
  const restoredColumns = merge({}, DefaultColumns[browserMode], customColumns, state.columns);
  // drop columns that were persisted in local storage, but are no longer supported:
  const validColumns = pickBy(has('name'), restoredColumns);
  commit('setColumns', validColumns);
}

const categoriesHash = ({ user, system } = {}) => {
  const groupHash = user ? user.group_hash : '';
  const categoriesTimeStamp = system ? system.category_last_change : '';
  const hash = [groupHash, categoriesTimeStamp].filter(Boolean).join(':');
  return hash;
};

async function fetchTree({ commit, getters, state }, mode = TreeMode.CATEGORY) {
  const { settings } = globals.I || {};

  commit('setTreeMode', mode);
  commit('setTreeStatus', Status.LOADING);

  const service = {
    [TreeMode.CATEGORY]: {
      endpoint: 'docbrowser_categories',
      params: { h: categoriesHash(settings) },
    },
    [TreeMode.DIRECTORY]: {
      endpoint: 'docbrowser_directories',
      params: { id: '/' },
    },
  }[mode];
  const response = await fetchService.GetData(service.endpoint, service.params || {});

  if (response.error) {
    logger.warn(response.message);
    showToast(response.message, response.error);
    commit('setTreeStatus', Status.ERROR);
    return;
  }
  const { treeSortBy } = state;
  const sortBy = (a, b) => ascending(a[treeSortBy], b[treeSortBy]);

  const treeNodes = response.data;
  const restoredNodes = Tree.map(treeNodes.sort(sortBy), (n) => ({
    ...n,
    ...(n.children && { children: n.children.sort(sortBy) }),
  }));
  commit('setTreeNodes', restoredNodes);

  const { selectedTreeNode } = getters;
  const parentIds = Tree.findParents(treeNodes, (n) => n.id === selectedTreeNode).map((n) => n.id);
  commit('setExpandedNodes', parentIds);

  commit('setTreeStatus', Status.RESOLVED);

  // temporary work-around for ICMS-3945
  // clear the Service worker cache if the tree is empty
  // TODO: fix the issue in the ServiceWorker; do not cache empty trees in the first place
  const { ServiceWorker } = globals?.I || {};
  if (ServiceWorker && !treeNodes.length) {
    ServiceWorker.deleteUserCache();
  }
}

async function fetchDirectories(_, node) {
  const response = await fetchService.GetData('docbrowser_directories', { id: node.id });

  if (response.error) {
    logger.warn(response.message);
    showToast(response.message, response.error);
    throw new Error('Cannot fetch directories', response.message);
  }

  return response.data;
}

async function fetchFilterParams({ state, commit }) {
  const response = await fetchService.GetData('docbrowser_filters_chips');

  if (response.error) {
    logger.warn(response.message);
    showToast(response.message, response.error);
    return;
  }

  // eslint-disable-next-line
  const { valid, tree } = validatePayload(response, chipSchema);

  const _converter = (array = []) => { // eslint-disable-line
    return array.reduce((map, param) => ({
      ...map,
      [param.name]: param,
    }), {});
  };

  const filterParams = _converter(tree.data);
  commit('setFilterParams', merge(filterParams, state.filterParams));
}

async function fetchSavedFilters({ commit }) {
  const response = await fetchService.GetData('docbrowser_get_saved_filters');

  if (response.error) {
    logger.warn(response.message);
    showToast(response.message, response.error);
    return;
  }

  // TODO: validate data shape
  commit('setSavedFilters', response.data);
}

// EXPORTED
async function fetchModuleData(context) {
  const { state } = context;
  fetchTree(context, state.treeMode);
  fetchFilterParams(context);
  fetchSavedFilters(context);
  fetchColumns(context);
}

async function saveFilter({ state, getters }, payload) {
  const response = await fetchService.GetData('docbrowser_filters_save', {
    name: payload.input,
    iql: getters.uiQuery,
    mode: state.filterMode,
    type: state.browserMode,
  });

  const { status } = response;
  const message = (status === 'ok') ? LANG.theFilterWasSaved : LANG.thereIsAlreadyAFilter;
  showToast(message, status !== 'ok');
}

let latestQuery;
async function fetchDocumentsPage({ commit }, payload) {
  const currentQuery = Date.now();
  latestQuery = currentQuery;

  commit('setDocumentsStatus', { status: Status.LOADING });
  const response = await fetchService.GetData('docbrowser_items_list', payload);
  const documents = response.data && response.data.documents;
  const totalDocumentsCount = response.data && response.data.total_documents_count;

  // ignore the results, if the query was changed in the meantime
  if (currentQuery !== latestQuery) {
    return false;
  }

  commit('setTotalDocumentsCount', totalDocumentsCount);

  if (response.error || !documents) {
    logger.warn(response.message || 'No documents provided by the back-end');
    commit('setDocumentsStatus', {
      status: Status.ERROR,
      message: response.message,
    });
    return false;
  }

  // FIXME: this pagination logic should not be here (EntityTable expects return value)
  // FIXME: do not return booleans from this action
  // If there are no documents its considered as last page
  // so we should stop requesting more pages.
  if (!documents.length) {
    commit('setDocumentsStatus', { status: Status.RESOLVED });
    return false;
  }

  const { page } = payload;
  commit('setDocuments', {
    page,
    // TODO: review/remove: data shape fix
    documents: documents.map((d) => ({ ...d, nodeId: d.node_id, master: true })),
  });
  commit('setDocumentsStatus', { status: Status.RESOLVED });

  return true;
}

async function fetchDocumentCopypages({ commit }, document) {
  const docId = document.node_id.match(/\d+$/);

  const response = await fetchService.GetData('docbrowser_items_copypages', { doc_id: docId });
  const copypages = response.data;
  if (response.error || !Array.isArray(copypages)) {
    logger.warn(response.message || 'Error in getting copypages from the backend.');
    return false;
  }

  // TODO: review/remove: maybe mutation is slow
  commit('setDocumentCopypages', {
    nodeId: document.node_id,
    // TODO: review/remove: data shape fix (same as for documents)
    copypages: copypages.map((c) => ({ ...c, nodeId: c.node_id })),
  });

  return true;
}

/**
 * Resets the IQL query based on the `filter` query parameter in the URL
 */
function restoreFilterQueryFromRoute({ commit, rootGetters }) {
  const { route } = rootGetters;
  const filterQueryParam = route?.query?.filter;
  if (!filterQueryParam) return;

  commit('resetFilterQuery', filterQueryParam);
}

function restoreCatQueryFromRoute({ commit, rootGetters }) {
  const { route } = rootGetters;
  const catQueryParam = route?.query?.cat;
  const nodeIdQueryParam = route?.query?.node_id;
  let catId;

  if (!catQueryParam) {
    if (!nodeIdQueryParam) return;
    catId = nodeIdQueryParam.replace(/\/\d+$/, ''); // remove the document from the node_id to get the cat_id
  } else if (catQueryParam.endsWith('+') || catQueryParam.endsWith(' ')) {
    commit('setTreeRecursiveMode', true);
    catId = catQueryParam.replace(/(\+|\s)/, ''); // remove the + and whitespace from the cat_id
  } else {
    commit('setTreeRecursiveMode', false);
    catId = catQueryParam;
  }

  if (catId === '/') {
    commit('selectTreeNode', '');
    return;
  }

  if (!catId.match(/(\/\d+)+$/)) return; // check if it is a valid cat_id
  commit('selectTreeNode', catId);
}

function checkInsertMode({ commit }) {
  const hasOpener = window.opener;
  if (hasOpener && hasOpener.I && hasOpener.I.MAM
      && hasOpener.I.MAM.options
      && hasOpener.I.MAM.options.name === hasOpener.I.MAM.currentName
      && hasOpener.I.MAM.options.instance_index === hasOpener.I.MAM.currentInstanceIndex) {
    commit('setInsertMode', true);
  }
}

function insertVariant(_, { nodeId, variantUri, contentLength }) {
  const { MAM } = globals.I;
  if (!MAM || !MAM.templateInjector) return;

  MAM.templateInjector({
    node_id: nodeId,
    variant_uri: variantUri,
    __imperia_mime_content_length: contentLength,
    errorMessage: 'Error inserting into a template: template instance not found.',
  });
}

async function checkUriLength(uri) {
  try {
    const response = await fetch(uri, { method: 'HEAD' }); // Use HEAD to check without fetching content
    return response.status !== 414; // Return true if status is not 414
  } catch (err) {
    logger.warn(err);
    return false; // If there's an error, assume the URI is too long
  }
}

/**
 * Opens the `multi upload` page with the specified documents for a given category
 */
async function openMultiUpload(_, { catId, tempFiles }) {
  try {
    if (!catId) throw new Error('Invalid cat id', catId);

    const uri = globals.I.Util.uri(
      '/document/media/multi_upload',
      {
        cat: catId,
        tempFiles,
      },
    );

    const isUriValid = await checkUriLength(uri);
    if (!isUriValid) {
      throw new Error(LANG.tooManyFiles);
    }

    globals.context.location.href = uri;
  } catch (err) {
    logger.warn(err);
    return err.message;
  }

  return '';
}

function sortTable({ getters, commit }, sort) {
  if (getters.ftsFilterEnabled) {
    commit('sortTableUserPreference', sort);
    return;
  }

  commit('sortTableUserPreference', null);
  commit('sortTableMainSetting', sort);
}

function clearSortUserPreferance({ commit }) {
  commit('sortTableUserPreference', null);
}

export default {
  fetchModuleData,
  fetchTree,
  fetchDirectories,
  fetchDocumentsPage,
  fetchDocumentCopypages,
  saveFilter,
  checkInsertMode,
  insertVariant,
  openMultiUpload,
  restoreFilterQueryFromRoute,
  restoreCatQueryFromRoute,
  sortTable,
  clearSortUserPreferance,
};
